import { DateTime } from "luxon";
import { convertTimestampToLuxonOrDefaultNow } from "../../utils/convertTimestampToLuxonOrDefaultNow";
export function assertRaveContent(content) {
    if (!content || typeof content !== "object") {
        throw new Error("RaveContent must be an object");
    }
    const c = content;
    if (!("type" in c)) {
        throw new Error("RaveContent must have a type field");
    }
    if (!["text", "image", "video", "audio"].includes(c.type)) {
        throw new Error("RaveContent.type must be one of: text, image, video, audio");
    }
    switch (c.type) {
        case "text":
            if (typeof c.text !== "string")
                throw new Error("RaveContent.text must be a string");
            break;
        case "image":
            if (typeof c.image !== "string")
                throw new Error("RaveContent.image must be a string");
            break;
        case "video":
            if (typeof c.video !== "string")
                throw new Error("RaveContent.video must be a string");
            break;
        case "audio":
            if (typeof c.audio !== "string")
                throw new Error("RaveContent.audio must be a string");
            break;
    }
}
export function assertRaveRecipient(recipient) {
    if (!recipient || typeof recipient !== "object") {
        throw new Error("RaveRecipient must be an object");
    }
    const r = recipient;
    if (typeof r.name !== "string") {
        throw new Error("RaveRecipient.name must be a string");
    }
}
export function assertRaveStatus(status) {
    if (!["draft", "submitted", "activated", "deactivated"].includes(status)) {
        throw new Error("RaveStatus must be one of: draft, submitted, activated, deactivated");
    }
}
export const assertRave = (data, mode = "load") => {
    if (!data || typeof data !== "object") {
        throw new Error("Rave must be an object");
    }
    const rave = data;
    // Required fields based on mode
    const requiredFields = mode === "save"
        ? [
            "programId",
            "content",
            "sender",
            "recipientIds",
            "recipients",
            "status",
        ]
        : [
            "id",
            "programId",
            "content",
            "sender",
            "recipientIds",
            "recipients",
            "status",
            "createdAt",
            "modifiedAt",
        ];
    for (const field of requiredFields) {
        if (!(field in rave)) {
            throw new Error(`Rave is missing required field: ${field}`);
        }
    }
    // Type checks
    if (mode === "load" && typeof rave.id !== "string")
        throw new Error("Rave.id must be a string");
    if (typeof rave.programId !== "string")
        throw new Error("Rave.programId must be a string");
    if (typeof rave.sender !== "object")
        throw new Error("Rave.sender must be an object");
    if (!Array.isArray(rave.recipientIds))
        throw new Error("Rave.recipientIds must be an array");
    // if (typeof rave.recipients !== 'object') throw new Error('Rave.recipients must be an object')
    // Validate status
    assertRaveStatus(rave.status);
    // Content validation
    const contents = rave.content;
    if (!Array.isArray(contents)) {
        throw new Error("Rave.content must be an array");
    }
    for (const content of contents) {
        assertRaveContent(content);
    }
    // Recipient validation
    const recipients = rave.recipients;
    for (const [, recipient] of Object.entries(recipients)) {
        assertRaveRecipient(recipient);
    }
    // Timestamp validation based on mode
    if (mode === "load") {
        if (!(rave.createdAt instanceof DateTime)) {
            throw new Error("Rave.createdAt must be a DateTime");
        }
        if (!(rave.modifiedAt instanceof DateTime))
            throw new Error("Rave.modifiedAt must be a DateTime");
    }
    else {
        // For save mode, we need to allow either DateTime or server timestamp
        // First check if fields exist
        if (rave.createdAt !== undefined) {
            // createdAt can be a DateTime or a FieldValue.serverTimestamp()
            // For server timestamp, it would be an object with an internal structure
            // specific to Firebase. Since we don't have a direct way to check,
            // we'll accept any object that's not a DateTime as a potential FieldValue
            const isDateTime = rave.createdAt instanceof DateTime;
            const isPotentialFieldValue = typeof rave.createdAt === "object" &&
                rave.createdAt !== null &&
                !isDateTime;
            if (!isDateTime && !isPotentialFieldValue) {
                throw new Error("Rave.createdAt must be a DateTime or FieldValue.serverTimestamp()");
            }
        }
        if (rave.modifiedAt !== undefined) {
            const isDateTime = rave.modifiedAt instanceof DateTime;
            const isPotentialFieldValue = typeof rave.modifiedAt === "object" &&
                rave.modifiedAt !== null &&
                !isDateTime;
            if (!isDateTime && !isPotentialFieldValue) {
                throw new Error("Rave.modifiedAt must be a DateTime or FieldValue.serverTimestamp()");
            }
        }
    }
    return true;
};
export const createRaveConverter = (convertToFirestoreTimestampOrDefaultServerTime) => ({
    toFirestore(rave) {
        if (!assertRave(rave, "save")) {
            throw new Error("Rave.save failed");
        }
        const { id, ...raveWithoutId } = rave;
        const data = {
            ...raveWithoutId,
            createdAt: convertToFirestoreTimestampOrDefaultServerTime(rave.createdAt),
            modifiedAt: convertToFirestoreTimestampOrDefaultServerTime(rave.modifiedAt),
        };
        return data;
    },
    fromFirestore(snapshot, options) {
        const data = snapshot.data(options);
        if (!data)
            throw new Error("Snapshot doesn't contain data");
        const rave = {
            ...data,
            id: snapshot.id,
            createdAt: convertTimestampToLuxonOrDefaultNow(data.createdAt),
            modifiedAt: convertTimestampToLuxonOrDefaultNow(data.modifiedAt),
        };
        if (!assertRave(rave, "load")) {
            throw new Error("Rave.load failed");
        }
        return rave;
    },
});
export const createRave = (data) => {
    if (!assertRave(data, "save")) {
        throw new Error("Rave.save failed");
    }
    return data;
};
//# sourceMappingURL=Rave.js.map